import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  connect as qzConnect,
  disconnect as qzDisconnect,
  isConnected as qzIsConnected,
  setClosedCallback,
  listPrinters as qzListPrinters,
  printRawZpl,
  printLaserSticker,
  buildZpl2x1,
  loadQzConfig,
  saveQzConfig,
} from '../utils/qzTrayService';

const QzTrayContext = createContext(null);

export function useQzTray() {
  return useContext(QzTrayContext);
}

export function QzTrayProvider({ children }) {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(loadQzConfig);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setClosedCallback(() => {
      if (mountedRef.current) {
        setConnected(false);
        setConnecting(false);
      }
    });
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (config.enabled && config.autoConnect && !qzIsConnected()) {
      setConnecting(true);
      qzConnect()
        .then(() => { if (mountedRef.current) { setConnected(true); setError(null); } })
        .catch((e) => { if (mountedRef.current) { setError(e?.message || 'QZ Tray not available.'); } })
        .finally(() => { if (mountedRef.current) { setConnecting(false); } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useCallback(async () => {
    if (qzIsConnected()) { setConnected(true); return; }
    setConnecting(true);
    setError(null);
    try {
      await qzConnect();
      setConnected(true);
    } catch (e) {
      const msg = e?.message || 'Could not connect to QZ Tray.';
      setError(msg);
      throw e;
    } finally {
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      await qzDisconnect();
    } finally {
      setConnected(false);
    }
  }, []);

  const listPrinters = useCallback(async () => {
    await connect();
    return qzListPrinters();
  }, [connect]);

  const updateConfig = useCallback((next) => {
    const merged = {
      ...config,
      ...next,
      printers: { ...config.printers, ...next?.printers },
    };
    saveQzConfig(merged);
    setConfig(merged);
  }, [config]);

  const printSticker = useCallback(async (payload) => {
    if (!config.enabled) return;
    if (config.printerMode === 'LASER') {
      try {
        printLaserSticker(payload);
      } catch (e) {
        toast.error(`Print failed: ${e?.message || 'Unknown error'}`);
      }
      return;
    }
    const printerName = config.printers?.STICKER;
    if (!printerName) return;
    try {
      const { style, ...stickerPayload } = payload || {};
      const zpl = buildZpl2x1(stickerPayload, style);
      await printRawZpl(printerName, zpl);
    } catch (e) {
      toast.error(`Print failed: ${e?.message || 'Unknown error'}`);
    }
  }, [config]);

  const value = {
    connected,
    connecting,
    error,
    config,
    connect,
    disconnect,
    listPrinters,
    updateConfig,
    printSticker,
  };

  return (
    <QzTrayContext.Provider value={value}>
      {children}
    </QzTrayContext.Provider>
  );
}
